import { useRef, useState } from "react";
import { FileText, Plus, Trash2, ArrowUp, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";
import { uploadChunked } from "@/lib/upload";

const inputCls = "w-full h-9 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const uid = () => `a${Math.random().toString(36).slice(2, 9)}`;

export const AssetsEditor = ({ form, set }) => {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const assets = form.assets;
  const upd = (i, patch) => set({ assets: assets.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  const upload = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Please upload a PDF file");
    setProgress(0);
    try {
      const d = await uploadChunked(file, setProgress);
      set({ assets: [...assets, { id: uid(), title: file.name.replace(/\.pdf$/i, ""), description: "", file_path: d.path, filename: d.filename }] });
      toast.success("White paper uploaded");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setProgress(null); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="assets-editor">
      <p className="text-xs text-slate-500 -mt-2">The first white paper is the main reading window; the others appear in the left panel as recommendations.</p>
      {assets.map((a, i) => (
        <div key={a.id} data-testid={`asset-row-${a.id}`} className="rounded-md border border-[#242C3D] bg-[#0B0F17]/60 p-4 space-y-2">
          <div className="flex items-center gap-2">
            {i === 0 ? <Star className="w-4 h-4 text-amber-400 shrink-0" /> : <FileText className="w-4 h-4 text-slate-500 shrink-0" />}
            <input data-testid={`asset-title-${a.id}`} value={a.title} onChange={(e) => upd(i, { title: e.target.value })} placeholder="Title *" className={inputCls} />
            {i > 0 && (
              <button type="button" data-testid={`asset-make-primary-${a.id}`} title="Make main asset" onClick={() => set({ assets: [a, ...assets.filter((_, j) => j !== i)] })}
                className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><ArrowUp className="w-4 h-4" /></button>
            )}
            <button type="button" data-testid={`asset-delete-${a.id}`} onClick={() => set({ assets: assets.filter((_, j) => j !== i) })}
              className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-rose-400 hover:bg-white/5"><Trash2 className="w-4 h-4" /></button>
          </div>
          <input value={a.description} onChange={(e) => upd(i, { description: e.target.value })} placeholder="Short description (shown in recommendations)" className={`${inputCls} text-xs`} />
          <p className="font-mono text-[11px] text-slate-500">{i === 0 ? "MAIN ASSET · " : ""}{a.filename}</p>
        </div>
      ))}
      <input ref={ref} type="file" accept="application/pdf" className="hidden" data-testid="asset-file-input" onChange={(e) => upload(e.target.files[0])} />
      <button type="button" data-testid="asset-upload-button" disabled={progress !== null || assets.length >= 10} onClick={() => ref.current?.click()}
        className="w-full rounded-lg border border-dashed border-[#2f3a50] hover:border-indigo-500 bg-[#0B0F17] py-6 flex items-center justify-center gap-2 text-sm text-slate-300 transition-colors disabled:opacity-50">
        {progress !== null ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</> : <><Plus className="w-4 h-4" /> Upload white paper (PDF)</>}
      </button>
      <div className="grid sm:grid-cols-2 gap-4 pt-2">
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Suggest next asset every (seconds of reading)</label>
          <input data-testid="recommend-after-input" type="number" min={10} max={3600} value={form.recommend_after_seconds}
            onChange={(e) => set({ recommend_after_seconds: Number(e.target.value) })} className={inputCls} />
        </div>
        <div>
          <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Content hub message</label>
          <input data-testid="hub-message-input" value={form.hub_message} onChange={(e) => set({ hub_message: e.target.value })} placeholder="Curated insights from Acme Corp" className={inputCls} />
        </div>
      </div>
    </div>
  );
};
